import { useMemo, type ReactNode } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { addDays, startOfDay, format } from '~/util/date';
import { DayCell } from './DayCell';
import { EventChip } from './EventChip';
import { useCalendarDnd } from './useCalendarDnd';
import type { CalendarEventModel, CellRenderer } from '../calendar/types';
import { Calendar } from 'lucide-react';
import { useToday } from '~/util/useToday';

//selectedCalendarView === 'First5Days'

export interface FiveDayTimelineProps<T extends CalendarEventModel> {
	events: T[];
	startDate: Date;
	numberOfDays?: number;
	today?: Date;
	label?: string;
	onCalendarItemClicked?: (event: T) => void;
	onCalendarCellClicked?: (date: Date) => void;
	onDragStopped?: (eventId: string, newStartTime: Date) => void;
	isDragAndDropValid?: (eventId: string, newStartTime: Date) => boolean;
	allowDragAndDrop?: boolean;
	renderCellContent?: CellRenderer;
	renderEventChip?: (event: T) => ReactNode;
	cellMinHeight?: number;
	legend?: ReactNode;
}

function toIsoDate(date: Date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

export function FiveDayTimeline<T extends CalendarEventModel>({
	events,
	startDate,
	numberOfDays = 5,
	label = `${5}-day timeline`,
	onCalendarItemClicked,
	onCalendarCellClicked,
	onDragStopped,
	isDragAndDropValid,
	allowDragAndDrop = false,
	renderCellContent,
	renderEventChip,
	cellMinHeight = 200,
	legend
}: FiveDayTimelineProps<T>) {
	const today = useToday();
	const { sensors, activeEvent, handleDragStart, handleDragEnd, handleDragCancel } = useCalendarDnd({
		events,
		onDragStopped,
		isDragAndDropValid
	});

	const cellDates = useMemo(() => {
		const start = startOfDay(startDate);
		const dates: Date[] = [];
		for (let i = 0; i < numberOfDays; i++) dates.push(addDays(start, i));
		return dates;
	}, [startDate, numberOfDays]);

	const eventsByDay = useMemo(() => {
		const map = new Map<string, T[]>();
		for (const event of events) {
			const key = toIsoDate(startOfDay(event.startTime));
			const bucket = map.get(key);
			if (bucket) bucket.push(event);
			else map.set(key, [event]);
		}
		for (const bucket of map.values()) bucket.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
		return map;
	}, [events]);

	const rangeStart = cellDates[0];
	const rangeEnd = cellDates[cellDates.length - 1];
	if (!rangeStart || !rangeEnd) {
		throw new Error('Invalid range: start or end date is missing.');
	}
	const gridCols = `repeat(${numberOfDays}, minmax(0, 1fr))`;

	return (
		<DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
			<div className="w-full">
				<div className="mb-2 flex items-center justify-between px-1">
					<div className="flex items-center gap-2 text-sm font-medium text-gray-900">
						<Calendar className="size-4 text-gray-500" aria-hidden="true" />
						<span>{`${format(rangeStart, 'MMM d')} – ${format(rangeEnd, 'MMM d, yyyy')}`}</span>
					</div>
					<div className="text-xs text-gray-500">{label}</div>
				</div>

				<div className="overflow-hidden rounded-md border border-gray-200">
					<div className="grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: gridCols }}>
						{cellDates.map(date => (
							<div key={`hdr-${toIsoDate(date)}`} className="px-3 py-2 text-center text-xs font-medium text-gray-500">
								{format(date, 'EEE').toUpperCase()}
							</div>
						))}
					</div>

					<div className="grid border-l border-t-0 border-gray-200" style={{ gridTemplateColumns: gridCols }}>
						{cellDates.map(date => {
							const key = toIsoDate(date);
							const dayEvents = eventsByDay.get(key) ?? [];
							return (
								<DayCell
									key={key}
									date={date}
									today={today}
									onCellClick={onCalendarCellClicked}
									renderCellContent={renderCellContent}
									droppable={allowDragAndDrop}
									eventLayout="stack"
									minHeight={cellMinHeight}
								>
									{dayEvents.map(event => (
										<EventChip
											key={event.id}
											event={event}
											onClick={onCalendarItemClicked}
											renderContent={renderEventChip}
											draggable={allowDragAndDrop}
											block
										/>
									))}
								</DayCell>
							);
						})}
					</div>
				</div>

				{legend && <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-600">{legend}</div>}
			</div>

			<DragOverlay>{activeEvent ? <EventChip event={activeEvent} renderContent={renderEventChip} block isOverlay /> : null}</DragOverlay>
		</DndContext>
	);
}
